import { useNavigate } from "react-router";
import { CheckCircle, XCircle, Bookmark } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSavedTutorials } from "@/hooks/useSavedTutorials";
import { useUser } from "@supabase/auth-helpers-react";
import { cn } from "@/lib/utils";
import { Dispatch, SetStateAction } from "react";
import { Tags } from "@/components/tutorial/TutorialTags";
import { useCategoryStore } from "@/stores/use-tutorial-progress";

enum PreferenceState {
  Selected = "included",
  Excluded = "excluded",
  Default = "default",
}

interface FilterButtonProps {
  icon: React.ReactNode;
  label: string;
  count: number;
  state: PreferenceState;
  onClick: () => void;
  onDoubleClick: (e: React.MouseEvent) => void;
}

const FilterButton = ({
  icon,
  label,
  count,
  state,
  onClick,
  onDoubleClick,
}: FilterButtonProps) => (
  <button
    onClick={onClick}
    onDoubleClick={onDoubleClick}
    className={cn(
      "group flex justify-center w-full px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300",
      {
        // When selected, the button is highlighted to show it's active
        "text-primary bg-primary/10 hover:bg-primary/20 active:bg-primary/20":
          state === PreferenceState.Selected,
        // Excluded state styling kept for other buttons that might use it
        "text-destructive line-through hover:bg-destructive/20 active:bg-destructive/20":
          state === PreferenceState.Excluded,
        "text-secondary-foreground dark:text-white hover:bg-secondary/90 dark:hover:bg-[#2A2A2A] active:bg-secondary/90 dark:active:bg-[#2A2A2A]":
          state === PreferenceState.Default,
      }
    )}
    title={
      state === PreferenceState.Selected
        ? `Currently showing only ${label.toLowerCase()} tutorials. Click to view all tutorials.`
        : `Click to show only ${label.toLowerCase()} tutorials.`
    }
  >
    <div className="flex items-center w-full">
      <div className="w-8 flex justify-center items-center">{icon}</div>
      <span className="transition-transform group-hover:scale-105">
        {label} ({count})
      </span>
    </div>
  </button>
);

interface SavedSidebarProps {
  uninterestedIds: string[];
  completedIds: string[];
  isLoading: boolean;
  tagPreferences: { included: string[]; excluded: string[] };
  onTagPreferenceChange: Dispatch<
    SetStateAction<{ included: string[]; excluded: string[] }>
  >;
  isLargeScreen?: boolean; // Optional prop to control width
}

export function SavedSidebar({
  uninterestedIds = [],
  completedIds = [],
  isLoading,
  tagPreferences,
  onTagPreferenceChange,
  isLargeScreen, // Optional prop to control width
}: SavedSidebarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useUser();
  const { savedTutorials = [], isLoading: isSavedLoading } = useSavedTutorials(
    user?.id
  );
  
  const { resetCurrentSelection } = useCategoryStore();

  const getPreferenceState = (tagName: string): PreferenceState => {
    if (!tagPreferences) return PreferenceState.Default;
    if (tagPreferences.included?.includes(tagName))
      return PreferenceState.Selected;
    if (tagPreferences.excluded?.includes(tagName))
      return PreferenceState.Excluded;
    return PreferenceState.Default;
  };

  const getNextState = (
    currentState: PreferenceState,
    isDoubleClick: boolean
  ): PreferenceState => {
    // For completed and not interested buttons, we only toggle between Selected and Default
    // When selected, it shows ONLY those tutorials
    // When default, it hides those tutorials (per updated filter logic)
    // We don't want to use Excluded state here as it's not needed with the new behavior

    // Double-click behavior changed to make more sense with the new UI
    if (isDoubleClick) {
      // Always reset to default on double-click
      return PreferenceState.Default;
    }

    // Toggle between Selected and Default - simple toggle behavior
    return currentState === PreferenceState.Selected
      ? PreferenceState.Default
      : PreferenceState.Selected;
  };

  const handlePreferenceUpdate = (
    currentState: PreferenceState,
    isDoubleClick: boolean,
    tagName: string
  ) => {
    resetCurrentSelection()
    if (!user?.id) return;
    const nextState = getNextState(currentState, isDoubleClick);
    const preferenceType =
      nextState === PreferenceState.Default
        ? null
        : nextState === PreferenceState.Selected
        ? "included"
        : "excluded";

    // Create updates for both the current tag and potentially its counterpart
    const updates = [];
    // Add the clicked tag's update
    updates.push({ tagName, preferenceType });

    // If we're setting a special filter to Selected state, ensure the other one is turned off
    if (preferenceType === "included") {
      const specialTags = ["completed", "not interested"];
      // Find the counterpart tag
      const otherTag = specialTags.find((tag) => tag !== tagName);

      if (otherTag && tagPreferences.included?.includes(otherTag)) {
        // If the other special tag is selected, deselect it
        updates.push({ tagName: otherTag, preferenceType: null });
      }
    }

    // Apply all updates one by one
    updates.forEach((update) => {
      onTagPreferenceChange((prev) => {
        let included = [...prev.included];
        let excluded = [...prev.excluded];
        // Remove from both first
        included = included.filter((t) => t !== update.tagName);
        excluded = excluded.filter((t) => t !== update.tagName);
        // Add to the correct list based on the new preference type
        if (update.preferenceType === "included") included.push(update.tagName);
        else if (update.preferenceType === "excluded")
          excluded.push(update.tagName);
        // If preferenceType is null, it's already removed from both
        return { included, excluded };
      });
    });
  };

  const filterButtons = [
    {
      icon: <CheckCircle className="w-5 h-5" />,
      label: t("tutorial.completion.completed"),
      count: completedIds?.length ?? 0,
      tagName: "completed",
      state: getPreferenceState("completed"),
    },
    {
      icon: <XCircle className="w-5 h-5" />,
      label: t("tags.notInterested"),
      count: uninterestedIds?.length ?? 0,
      tagName: "not interested",
      state: getPreferenceState("not interested"),
    },
  ];

  // Get the 4 most recent saved tutorials for preview
  const recentSavedTutorials = savedTutorials.slice(0, 4);

  if (isLoading || isSavedLoading) {
    return (
      <div
        className={cn("p-2 md:p-4", {
          "w-80": isLargeScreen,
          "w-full": !isLargeScreen,
        })}
      >
        <div className="space-y-2 md:space-y-4">
          <div>
            <div className="h-7 w-48 bg-gray-200 rounded-lg animate-pulse mb-2 md:mb-4" />
            <div className="h-10 w-full bg-gray-200 rounded-lg animate-pulse mb-2 md:mb-4" />
          </div>
          {/* Loading placeholders for tutorial previews */}
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="animate-pulse space-y-2 mb-2 md:mb-4">
              <div className="h-16 bg-gray-200 rounded-md" />
            </div>
          ))}
          {[1, 2].map((i) => (
            <div key={`filter-${i}`} className="border-t pt-2 md:pt-4">
              <div className="h-10 w-full bg-gray-200 rounded-lg animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn("p-3", {
        "w-full": !isLargeScreen,
        "w-80": isLargeScreen,
      })}
    >
      <div className="space-y-3">
        <div className="text-center md:text-left">
          <h2
            className={cn(
              "text-xl font-semibold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent  md:text-left",
              { "text-center": isLargeScreen, "text-left": !isLargeScreen }
            )}
          >
            {t("common.savedTutorials")}
          </h2>
        </div>

        {/* Recent saved tutorials preview */}
        {recentSavedTutorials.length > 0 && (
          <div className="space-y-3 mt-3">
            {recentSavedTutorials.map((tutorial) => (
              <div
                key={tutorial.id}
                className={cn(
                  "group flex items-start justify-between gap-3 rounded-lg  transition-all duration-300 hover:bg-secondary/50 dark:hover:bg-secondary/10 cursor-pointer",
                  {
                    "p-2": isLargeScreen,
                    "px-5": !isLargeScreen,
                  }
                )}
                onClick={() => navigate(`/tutorial/${tutorial.documentId}`)}
              >
                <div className="flex gap-2">
                  <div className="w-16 h-12 rounded-md overflow-hidden flex-shrink-0 bg-muted">
                    <img
                      src={tutorial.thumbnailUrl || "/placeholder.svg"}
                      alt={tutorial.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-medium leading-tight text-foreground group-hover:text-primary transition-colors line-clamp-2">
                      {tutorial.title}
                    </h3>
                    <div className="mt-1">
                      <Tags tags={tutorial.tags.slice(0, 2)} />
                    </div>
                  </div>
                </div>
                {/*<div className="text-primary h-8 w-8 shrink-0 flex items-center justify-center">
                  <Bookmark className="h-5 w-5 fill-current" />
                </div>*/}
              </div>
            ))}
          </div>
        )}

        {/* Show All button */}
        <div className="flex justify-center mt-3 w-full">
          <button
            onClick={() => navigate("/saved?reset=true")}
            className="group flex justify-center w-full px-4 py-2 text-secondary-foreground dark:text-white hover:bg-secondary/90 dark:hover:bg-[#2A2A2A] active:bg-secondary/90 dark:active:bg-[#2A2A2A] rounded-lg transition-all duration-300 text-sm font-medium"
          >
            <div className="flex items-center w-full">
              <div className="w-8 flex justify-center items-center">
                <Bookmark size={16} />
              </div>
              <span className="transition-transform group-hover:scale-105">
                {t("common.showAll")} ({savedTutorials?.length ?? 0})
              </span>
            </div>
          </button>
        </div>

        <div className="border-t pt-3 flex flex-col items-center md:items-start">
          <div className="flex flex-wrap gap-2 justify-center md:justify-start w-full">
            {filterButtons.map((button) => (
              <FilterButton
                key={button.tagName}
                {...button}
                onClick={() =>
                  handlePreferenceUpdate(button.state, false, button.tagName)
                }
                onDoubleClick={(e) => {
                  e.preventDefault();
                  handlePreferenceUpdate(button.state, true, button.tagName);
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
